import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Button, Flex, Switch } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SPACE_MD } from '@/theme/buildAntdTheme';
import { SettingsPopover } from '@/UI/DataTable/SettingsPopover';
import { createDefaultDataTableLayout } from '@/UI/DataTable/types';
import { Caption, LabelTitle, Meta } from '@/UI/typography';

import type { DataTableColumn, DataTableLayout } from '@/UI/DataTable/types';

type DataTableSettingsProps<T> = {
  columns: DataTableColumn<T>[];
  layout: DataTableLayout;
  onChange: (layout: DataTableLayout) => void;
};

export function DataTableSettings<T>({
  columns,
  layout,
  onChange,
}: DataTableSettingsProps<T>) {
  const { t } = useTranslation();
  const hidden = useMemo(() => new Set(layout.hidden), [layout.hidden]);
  const orderIndex = useMemo(
    () => new Map(layout.order.map((id, index) => [id, index])),
    [layout.order],
  );
  const configurable = useMemo(
    () =>
      columns
        .filter((column) => column.isAvailable !== false && !column.locked)
        .sort(
          (a, b) =>
            (orderIndex.get(a.id) ?? columns.length) -
            (orderIndex.get(b.id) ?? columns.length),
        ),
    [columns, orderIndex],
  );
  const visibleCount = configurable.filter(
    (column) => !hidden.has(column.id),
  ).length;

  const moveColumn = (id: string, offset: -1 | 1) => {
    const order = layout.order.filter((key) =>
      columns.some(
        (column) => column.id === key && column.isAvailable !== false,
      ),
    );
    const currentIndex = order.indexOf(id);
    const nextIndex = currentIndex + offset;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= order.length) return;
    [order[currentIndex], order[nextIndex]] = [
      order[nextIndex],
      order[currentIndex],
    ];
    onChange({ ...layout, order });
  };

  const content = (
    <Flex vertical>
      <Flex vertical gap={2}>
        <LabelTitle>{t('TABLE_SETTINGS')}</LabelTitle>
        <Meta>{t('TABLE_SETTINGS_DESCRIPTION')}</Meta>
      </Flex>
      <Flex vertical>
        {configurable.map((column, index) => {
          const visible = !hidden.has(column.id);
          return (
            <Flex
              key={column.id}
              align="center"
              justify="space-between"
              gap={SPACE_MD}
            >
              <Caption ellipsis>{column.title}</Caption>
              <Flex align="center" gap={4}>
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  aria-label={t('MOVE_COLUMN_UP')}
                  onClick={() => moveColumn(column.id, -1)}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === configurable.length - 1}
                  aria-label={t('MOVE_COLUMN_DOWN')}
                  onClick={() => moveColumn(column.id, 1)}
                />
                <Switch
                  size="small"
                  checked={visible}
                  disabled={visible && visibleCount === 1}
                  onChange={(checked) => {
                    const next = new Set(layout.hidden);
                    if (checked) next.delete(column.id);
                    else next.add(column.id);
                    onChange({ ...layout, hidden: [...next] });
                  }}
                />
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      <Button
        size="small"
        onClick={() => onChange(createDefaultDataTableLayout(columns))}
      >
        {t('RESET_LAYOUT')}
      </Button>
    </Flex>
  );

  return (
    <SettingsPopover
      content={content}
      tooltip={t('CUSTOMIZE_TABLE')}
      aria-label={t('CUSTOMIZE_TABLE')}
      data-testid="table-settings-button"
    />
  );
}
